import React, { useRef, useState } from 'react';
import './FileUpload.scss';
import { useTranslation } from '../i18n';

const FileUpload = ({ onFileSelect, disabled = false }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isInspectionDialogOpen, setIsInspectionDialogOpen] = useState(false);
  const [inspectionDamagePercent, setInspectionDamagePercent] = useState('');
  const pendingInspectionPercentRef = useRef(null);
  const fileInputRef = useRef(null);

  const openFilePicker = async (percent) => {
    pendingInspectionPercentRef.current = percent;
    setIsInspectionDialogOpen(false);

    const desktopPicker = typeof window !== 'undefined' && window.desktopApi?.selectGeoTiff;
    if (desktopPicker) {
      const selected = await desktopPicker();
      if (!selected || !selected.name) {
        return;
      }

      const file = new File([selected.buffer], selected.name, {
        type: 'image/tiff',
      });

      await onFileSelect(file, pendingInspectionPercentRef.current);
      pendingInspectionPercentRef.current = null;
      return;
    }

    const input = fileInputRef.current;
    if (!input) return;

    input.value = '';

    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }

    requestAnimationFrame(() => {
      input.click();
    });
  };

  const handleInspectionSubmit = async (event) => {
    event.preventDefault();
    const percent = inspectionDamagePercent === '' ? null : Number(inspectionDamagePercent);
    if (percent !== null && (!Number.isFinite(percent) || percent < 0 || percent > 100)) return;
    await openFilePicker(percent);
  };

  const handleSkipInspection = async () => {
    setInspectionDamagePercent('');
    await openFilePicker(null);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsLoading(true);

      try {
        await onFileSelect(file, pendingInspectionPercentRef.current);
      } finally {
        setIsLoading(false);
        pendingInspectionPercentRef.current = null;
        // Allow selecting the same file again (input change event would not fire otherwise).
        event.target.value = '';
      }
    }
  };

  return (
    <div className="file-upload">
      <div className="upload-wrapper">
        <input
          ref={fileInputRef}
          type="file"
          id="geotiff-input"
          accept=".tif,.tiff"
          onChange={handleFileChange}
          disabled={disabled || isLoading}
          className="file-input"
        />
        <button
          type="button"
          className="upload-label"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsInspectionDialogOpen(true);
          }}
          disabled={disabled || isLoading}
        >
          {!isLoading && (
            <svg className="upload-label__icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 16V4" />
              <path d="m7.5 8.5 4.5-4.5 4.5 4.5" />
              <path d="M5 14.5v4A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5v-4" />
            </svg>
          )}
          <span>{isLoading ? t('fileUpload.processing') : t('fileUpload.choose')}</span>
          {!isLoading && (
            <svg className="upload-label__icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 16V4" />
              <path d="m7.5 8.5 4.5-4.5 4.5 4.5" />
              <path d="M5 14.5v4A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5v-4" />
            </svg>
          )}
        </button>
      </div>
      {isInspectionDialogOpen && (
        <div className="inspection-dialog-backdrop" onMouseDown={() => setIsInspectionDialogOpen(false)}>
          <div
            className="inspection-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="inspection-dialog-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <form onSubmit={handleInspectionSubmit}>
              <div className="inspection-dialog__heading">
                <span className="inspection-dialog__icon" aria-hidden="true">%</span>
                <div>
                  <h2 id="inspection-dialog-title">{t('inspectionDialog.title')}</h2>
                  <p>{t('inspectionDialog.description')}</p>
                </div>
              </div>
              <label htmlFor="inspection-damage-percent">{t('inspectionDialog.percentLabel')}</label>
              <div className="inspection-dialog__input-row">
                <input
                  id="inspection-damage-percent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={inspectionDamagePercent}
                  onChange={(event) => setInspectionDamagePercent(event.target.value)}
                  autoFocus
                />
                <span>%</span>
              </div>
              <p className="inspection-dialog__note">{t('inspectionDialog.note')}</p>
              <div className="inspection-dialog__actions">
                <button type="button" className="is-secondary" onClick={() => setIsInspectionDialogOpen(false)}>
                  {t('common.cancel')}
                </button>
                <button type="button" className="is-secondary" onClick={handleSkipInspection}>
                  {t('inspectionDialog.skip')}
                </button>
                <button type="submit" className="is-primary">{t('inspectionDialog.continue')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
